import {
  DEFAULT_MAX_DAILY_TRADES,
  DEFAULT_MAX_SINGLE_TRADE_AMOUNT,
  DEFAULT_PRICE_TOLERANCE_THRESHOLD,
  createDefaultMappingFormState,
} from './riskParameters.js';

export function createInitialFormState() {
  return {
    email: '',
    password: '',
    confirmPassword: '',
    tradierToken: '',
    tradierAccountId: '',
    discordUserId: '',
    phoneNumber: '',
    priceDeviationLimit: DEFAULT_PRICE_TOLERANCE_THRESHOLD,
    enablePriceChecks: true,
    maxDailyTrades: DEFAULT_MAX_DAILY_TRADES,
    maxSingleTradeAmount: DEFAULT_MAX_SINGLE_TRADE_AMOUNT,
    maxDailyDollarExposure: DEFAULT_MAX_SINGLE_TRADE_AMOUNT,
    autoApproveEnabled: false,
    positionMultiplier: 1,
    tradierUseSandbox: false,
  };
}

export function createInitialUserSettings() {
  return {
    discordNotifications: true,
    emailNotifications: false,
    smsCriticalAlerts: false,
    smsPhoneNumber: '',
  };
}

export function createInitialAccountSettings() {
  return {
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    newPhoneNumber: '',
  };
}

export function createInitialValidationState() {
  return {
    discordUserId: { status: 'pending', message: '' },
    tradierAccountId: { status: 'pending', message: '' },
    tradierToken: { status: 'pending', message: '' },
  };
}

export function createInitialMappingValidationState() {
  return {
    apiToken: { status: 'pending', message: '' },
    accountId: { status: 'pending', message: '' },
    discordUserId: { status: 'pending', message: '' },
  };
}

export function createInitialHistoryFilters() {
  return {
    symbol: '',
    action: '',
    status: '',
    trade_type: '',
    environment: '',
    date_from: '',
    date_to: '',
    min_amount: '',
    max_amount: '',
    sort_by: 'created_at',
    sort_order: 'DESC',
    limit: 50,
    offset: 0,
    channel_id: '',
    trading_platform: '',
  };
}

export function createInitialAdminUserSummary() {
  return {
    totalUsers: 0,
    activeUsers: 0,
    configuredUsers: 0,
  };
}

export function createInitialChannelEditForm() {
  return { channel_name: '', guild_name: '' };
}

/** Snapshot shape used by tests to assert logout / user-switch cleared sensitive state. */
export function createClearedUserSessionSnapshot() {
  return {
    formState: createInitialFormState(),
    userSettings: createInitialUserSettings(),
    accountSettings: createInitialAccountSettings(),
    userData: null,
    tradeHistory: [],
    tradeHistoryError: '',
    tradingAnalytics: {},
    channelMappings: [],
    availableChannels: [],
    availablePlatforms: [],
    selectedUserTrades: [],
    selectedUserMappings: [],
    tradierAccountId: '',
    tradierToken: '',
  };
}

const SENSITIVE_STRING_FIELDS = [
  ['formState', 'tradierToken'],
  ['formState', 'tradierAccountId'],
  ['formState', 'discordUserId'],
];

/** Returns true when snapshot still contains user-specific data that must be cleared on logout. */
export function sessionSnapshotHasSensitiveData(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return false;

  if (Array.isArray(snapshot.tradeHistory) && snapshot.tradeHistory.length > 0) return true;
  if (Array.isArray(snapshot.channelMappings) && snapshot.channelMappings.length > 0) return true;
  if (Array.isArray(snapshot.selectedUserTrades) && snapshot.selectedUserTrades.length > 0) return true;
  if (Array.isArray(snapshot.selectedUserMappings) && snapshot.selectedUserMappings.length > 0) return true;
  if (snapshot.userData != null) return true;

  for (const [objKey, fieldKey] of SENSITIVE_STRING_FIELDS) {
    const value = snapshot[objKey]?.[fieldKey];
    if (typeof value === 'string' && value.trim() !== '') return true;
  }

  return false;
}

export function isSessionSnapshotCleared(snapshot) {
  return !sessionSnapshotHasSensitiveData(snapshot);
}

export { createDefaultMappingFormState };
